import React from 'react';
import { Target, Info } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * La largeur du ciblage qu'un concurrent a déclarée à Meta.
 *
 * ## Ce que cette carte montre, et la raison d'être de son pied de page
 *
 * Le règlement européen sur les services numériques oblige Meta à publier l'âge, le genre et
 * les zones visées par **toute** annonce diffusée dans l'Union. Kokpit rapatriait ces colonnes
 * depuis l'origine et ne les relisait nulle part.
 *
 * ⚠️ **Elles disent la largeur, pas le détail.** Relevé sur 231 lignes : `All` partout,
 * `18-65` partout, aucune exclusion. Ce n'est pas une donnée manquante — c'est le constat
 * « ce concurrent n'a rien restreint », donc l'indice d'un ciblage large ou d'un Advantage+.
 * Mais sans la phrase de `limite`, « 18-65 · Tous · France » se lit « voici son audience », ce
 * qui est faux : on ne sait pas si elle vient d'un retargeting, d'une liste client ou d'un
 * lookalike. Le pied de carte est donc **toujours** affiché, jamais conditionné.
 *
 * ⚠️ **Et la phrase vient du serveur.** `limite` et les `pourquoi` des axes sont rédigés dans
 * `routes/competitors/ciblageDsa.js`. Les réécrire ici en ferait une seconde rédaction, qui
 * divergerait au premier seuil ajusté — et c'est la phrase, non le code du verdict, que
 * l'utilisateur lit.
 */

/** Une couleur par verdict. `INDETERMINE` est gris : il ne dit rien, il ne doit rien suggérer. */
const COULEURS = {
  OUVERT: { fond: '#eff6ff', bord: '#bfdbfe', texte: '#1d4ed8', libelle: 'Audience ouverte' },
  RESSERRE: { fond: '#fef3c7', bord: '#fde68a', texte: '#92400e', libelle: 'Ciblage resserré' },
  MIXTE: { fond: '#f5f3ff', bord: '#ddd6fe', texte: '#6d28d9', libelle: 'Ciblage mixte' },
  INDETERMINE: { fond: '#f9fafb', bord: '#e5e7eb', texte: '#6b7280', libelle: 'Non publié' }
};

/** Un repère par état d'axe. ⚠️ « non mesuré » n'emprunte pas la couleur d'« ouvert ». */
const ETATS = {
  ouvert: { point: '#3b82f6', libelle: 'ouvert' },
  resserre: { point: '#f59e0b', libelle: 'resserré' },
  partiel: { point: '#8b5cf6', libelle: 'partiel' },
  non_mesure: { point: '#d1d5db', libelle: 'non publié' }
};

const LIBELLES_AXE = { age: 'Âge', genre: 'Genre', zones: 'Zones' };

const VerdictLargeurCiblage = ({ ciblage }) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  if (!ciblage) return null;

  const couleur = COULEURS[ciblage.verdict] || COULEURS.INDETERMINE;

  return (
    <div style={{
      padding: '20px',
      borderRadius: '12px',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb'
    }}>
      <h3 style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '4px'
      }}>
        <Target size={16} />
        Largeur du ciblage publié
      </h3>
      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
        Ce que Meta publie au titre du règlement européen (DSA)
      </p>

      <div style={{
        display: 'inline-block',
        padding: '6px 12px',
        borderRadius: '999px',
        backgroundColor: couleur.fond,
        border: `1px solid ${couleur.bord}`,
        color: couleur.texte,
        fontSize: '13px',
        fontWeight: '600',
        marginBottom: '16px'
      }}>
        {couleur.libelle}
      </div>

      {/* Les trois axes, chacun avec sa phrase. ⚠️ Un axe non publié est affiché lui aussi :
          son absence est une information, et le masquer ramènerait le silence d'avant. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {(ciblage.axes || []).map(axe => {
          const etat = ETATS[axe.etat] || ETATS.non_mesure;
          return (
            <div key={axe.axe} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: etat.point,
                marginTop: '6px',
                flexShrink: 0
              }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '500' }}>
                  {LIBELLES_AXE[axe.axe] || axe.axe}
                  <span style={{ color: '#9ca3af', fontWeight: '400' }}> · {etat.libelle}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                  {axe.pourquoi}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ⚠️ Les créas sans ciblage publié, nommées. Les taire ferait porter le verdict sur un
          dénominateur que personne ne connaît. */}
      {ciblage.creasSansCiblage > 0 && (
        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '14px', marginBottom: 0 }}>
          {ciblage.creasSansCiblage} créa{ciblage.creasSansCiblage > 1 ? 's' : ''} ne publie
          {ciblage.creasSansCiblage > 1 ? 'nt' : ''} aucun ciblage et n'entre
          {ciblage.creasSansCiblage > 1 ? 'nt' : ''} pas dans ce constat.
        </p>
      )}

      {/* ⚠️ Toujours affiché, jamais conditionné : c'est la phrase qui empêche de lire cette
          carte comme un profil d'audience. */}
      <div
        className="ksi-survolable"
        onMouseEnter={survoler(ciblage.limite)}
        onMouseLeave={quitter}
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-start',
          marginTop: '16px',
          paddingTop: '14px',
          borderTop: '1px solid #f3f4f6',
          fontSize: '11px',
          color: '#9ca3af',
          lineHeight: 1.5
        }}
      >
        <Info size={13} style={{ flexShrink: 0, marginTop: '2px' }} />
        <span>{ciblage.limite}</span>
      </div>

      {bulle}
    </div>
  );
};

export default VerdictLargeurCiblage;
